// «Pasaporte del equipo» — ficha pública por número de serie (sin login),
// ruta /pasaporte/:token. Estética «CARFAX gamer»: sirve para que el comprador
// de un equipo usado compare el serial físico con el registrado y vea el
// historial de reparaciones, garantía y repuestos. SIN datos del dueño (LOPDP):
// solo el número de dueños registrados y la fecha del último traspaso.
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, BadgeCheck, CalendarClock, CheckCircle2, Cpu, ExternalLink, Fingerprint, Gamepad2, History,
  Loader2, Lock, Repeat, ShieldAlert, ShieldCheck, ThermometerSun, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { tallerRpc, fmtFecha } from './shared';

// ── Contrato de taller.pasaporte(_token) ────────────────────────────────────
interface PasaportePieza { tipo: 'diagnostico' | 'mano_obra' | 'repuesto'; descripcion: string; original: boolean; con_serie: boolean; garantia_dias: number | null }
interface PasaporteReparacion {
  codigo: string; fecha: string | null; falla: string; trabajo: string | null; garantia_hasta: string | null;
  en_garantia: boolean; reingreso: boolean; bench: Record<string, unknown> | null; piezas: PasaportePieza[];
  /** 955: código del certificado de bench firmado/emitido para esta reparación (null si no se certificó). */
  certificado: string | null;
}
interface PasaporteResp {
  taller: { nombre: string; garantia_dias: number } | null;
  equipo: { tipo: string; marca: string | null; modelo: string | null; serial: string | null; color: string | null; registrado: string };
  garantia_hasta: string | null;
  duenos: number;
  ultimo_traspaso: string | null;
  traspaso_activo: boolean;
  reparaciones: PasaporteReparacion[];
}
interface RespuestaReclamo { ok: boolean; mensaje: string }

/** URL pública de verificación del certificado de bench (destino del QR del PDF). */
const urlVerificarBench = (codigo: string) => `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}/verificar-bench/${codigo}`;

const BENCH_LABELS: Record<string, string> = {
  temp_antes: 'Temperatura antes', temp_despues: 'Temperatura después', juego: 'Juego de prueba',
  fps: 'FPS', horas: 'Horas de prueba', hdmi: 'HDMI',
};
const benchLabel = (k: string) => BENCH_LABELS[k] ?? (k.charAt(0).toUpperCase() + k.slice(1)).replace(/_/g, ' ');

function Seccion({ icono, titulo, children, acento }: { icono: React.ReactNode; titulo: string; children: React.ReactNode; acento?: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm">
      <h2 className={cn('mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide', acento ?? 'text-cyan-300')}>{icono} {titulo}</h2>
      {children}
    </div>
  );
}

function Bench({ bench }: { bench: Record<string, unknown> }) {
  const entradas = Object.entries(bench).filter(([, v]) => v !== null && v !== '');
  if (entradas.length === 0) return null;
  return (
    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 rounded-lg border border-white/10 bg-black/20 p-2.5 sm:grid-cols-3">
      {entradas.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <p className="truncate text-[10px] uppercase tracking-wide text-white/40">{benchLabel(k)}</p>
          <p className="truncate text-xs font-semibold text-white/85">{String(v)}</p>
        </div>
      ))}
    </div>
  );
}

function PiezaBadge({ p }: { p: PasaportePieza }) {
  if (p.tipo === 'diagnostico') return null;
  return (
    <li className="flex items-start gap-2 text-sm">
      <Cpu className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-300" />
      <div className="min-w-0 flex-1">
        <span className="text-white/85">{p.descripcion}</span>
        {p.tipo === 'repuesto' && (
          <span className={cn('ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
            p.original ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300')}>
            {p.original ? 'Original' : 'Alternativo'}
          </span>
        )}
        {p.con_serie && <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] text-white/40"><Fingerprint className="h-3 w-3" /> con número de serie</span>}
        {p.garantia_dias != null && <span className="ml-1.5 text-[10px] text-white/40">· garantía {p.garantia_dias} días</span>}
      </div>
    </li>
  );
}

export function Pasaporte() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const [form, setForm] = useState({ codigo: '', nombre: '', identificacion: '', celular: '', email: '', acepta: false });
  const [resultado, setResultado] = useState<RespuestaReclamo | null>(null);

  const { data, isLoading, isError, error } = useQuery<PasaporteResp>({
    queryKey: ['taller-pasaporte', token],
    queryFn: () => tallerRpc<PasaporteResp>('pasaporte', { _token: token }),
    retry: 1,
    enabled: !!token,
  });

  const reclamar = useMutation({
    mutationFn: () => tallerRpc<RespuestaReclamo>('pasaporte_reclamar', {
      _token: token, _codigo: form.codigo.trim().toUpperCase(), _nombre: form.nombre.trim(),
      _identificacion: form.identificacion.trim(), _celular: form.celular.trim() || null,
      _email: form.email.trim() || null, _acepta: form.acepta,
    }),
    onSuccess: (r) => {
      setResultado(r);
      if (r.ok) {
        toast.success('Traspaso registrado: ya eres el nuevo dueño registrado');
        void qc.invalidateQueries({ queryKey: ['taller-pasaporte', token] });
      }
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-violet-950 via-slate-950 to-cyan-950 text-white">
        <Loader2 className="mr-2 h-6 w-6 animate-spin text-fuchsia-400" /> Cargando el pasaporte del equipo…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gradient-to-br from-violet-950 via-slate-950 to-cyan-950 p-6 text-center text-white">
        <AlertTriangle className="h-10 w-10 text-amber-400" />
        <p className="text-xl font-bold">Pasaporte no encontrado</p>
        <p className="max-w-sm text-sm text-white/60">{getErrorMessage(error) || 'El enlace o el código QR no corresponde a ningún equipo registrado.'}</p>
      </div>
    );
  }

  const o = data;
  const hoy = new Date();
  const vigente = !!o.garantia_hasta && new Date(`${o.garantia_hasta}T23:59:59`) >= hoy;
  const nombreEquipo = [o.equipo.marca, o.equipo.modelo].filter(Boolean).join(' ') || o.equipo.tipo;
  const puedeEnviar = form.codigo.trim().length === 8 && form.nombre.trim().length >= 3 && form.identificacion.trim().length >= 6 && form.celular.trim().length >= 7 && form.acepta;

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-slate-950 to-cyan-950 pb-10 text-white">
      {/* Encabezado */}
      <header className="border-b border-white/10 bg-black/20 px-4 py-5">
        <div className="mx-auto max-w-2xl">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-fuchsia-300"><History className="h-3.5 w-3.5" /> Pasaporte del equipo</p>
          <h1 className="mt-0.5 text-lg font-extrabold">{o.taller?.nombre ?? 'Taller'}</h1>
          <p className="mt-2 flex items-center gap-2 text-2xl font-black"><Gamepad2 className="h-6 w-6 text-cyan-300" /> {nombreEquipo}</p>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 pt-5">
        {/* Serial grande — comparar con la etiqueta física */}
        <Seccion icono={<Fingerprint className="h-4 w-4" />} titulo="Número de serie">
          <p className="rounded-lg border border-white/15 bg-black/30 px-4 py-3 text-center font-mono text-2xl font-black tracking-widest text-white">
            {o.equipo.serial || '— sin registrar —'}
          </p>
          <p className="mt-2 text-center text-xs text-white/50">Compárelo con la etiqueta física del equipo antes de comprarlo.</p>
          <div className="mt-3 flex flex-wrap justify-center gap-2 text-xs text-white/60">
            {o.equipo.color && <span className="rounded-full bg-white/10 px-3 py-1">Color {o.equipo.color}</span>}
            <span className="rounded-full bg-white/10 px-3 py-1">Registrado {fmtFecha(o.equipo.registrado)}</span>
          </div>
        </Seccion>

        {/* Sello de garantía */}
        <div className={cn('flex items-center gap-3 rounded-xl border px-4 py-3', vigente ? 'border-emerald-400/40 bg-emerald-500/10' : 'border-white/15 bg-white/[0.03]')}>
          {vigente ? <ShieldCheck className="h-8 w-8 shrink-0 text-emerald-300" /> : <ShieldAlert className="h-8 w-8 shrink-0 text-white/40" />}
          <div>
            <p className={cn('text-sm font-bold', vigente ? 'text-emerald-200' : 'text-white/70')}>
              {vigente ? `Garantía vigente hasta ${fmtFecha(o.garantia_hasta)}` : 'Sin garantía vigente'}
            </p>
            <p className="text-xs text-white/50">{vigente ? 'La garantía viaja con el equipo, no con el dueño: si lo compra, la mantiene.' : 'Este equipo no tiene garantía activa por el taller.'}</p>
          </div>
        </div>

        {/* Dueños — sin nombres (LOPDP) */}
        <Seccion icono={<Lock className="h-4 w-4" />} titulo="Historial de propiedad">
          <div className="flex flex-wrap gap-4 text-sm">
            <div><p className="text-xs text-white/50">Dueños registrados</p><p className="text-lg font-bold">{o.duenos}</p></div>
            <div><p className="text-xs text-white/50">Último traspaso</p><p className="text-lg font-bold">{o.ultimo_traspaso ? fmtFecha(o.ultimo_traspaso) : '—'}</p></div>
          </div>
          <p className="mt-2 text-[11px] text-white/40">Esta ficha es pública y no incluye nombres ni datos de contacto del dueño (protección de datos personales).</p>
        </Seccion>

        {/* Línea de tiempo de reparaciones */}
        <Seccion icono={<Wrench className="h-4 w-4" />} titulo={`Reparaciones (${o.reparaciones.length})`}>
          {o.reparaciones.length === 0 ? (
            <p className="text-sm text-white/50">Este equipo no tiene reparaciones entregadas registradas.</p>
          ) : (
            <ol className="space-y-4">
              {o.reparaciones.map((r) => (
                <li key={r.codigo} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{r.codigo}</span>
                    <span className="text-xs text-white/40">{fmtFecha(r.fecha)}</span>
                    {r.en_garantia && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-300"><ShieldCheck className="h-3 w-3" /> Cubierto por garantía</span>}
                    {r.reingreso && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-300"><Repeat className="h-3 w-3" /> Reingreso</span>}
                    {r.certificado && (
                      <a href={urlVerificarBench(r.certificado)} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-full bg-cyan-500/20 px-2 py-0.5 text-[10px] font-semibold text-cyan-300 underline-offset-2 hover:underline">
                        <BadgeCheck className="h-3 w-3" /> Bench certificado ✓ <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    )}
                  </div>
                  <p className="mt-1.5 text-sm text-white/80">{r.falla}</p>
                  {r.trabajo && <p className="mt-0.5 text-xs text-white/50">{r.trabajo}</p>}
                  {r.garantia_hasta && <p className="mt-1 text-[11px] text-white/40">Garantía de este trabajo hasta {fmtFecha(r.garantia_hasta)}</p>}
                  {r.piezas.length > 0 && (
                    <ul className="mt-2 space-y-1 border-t border-white/10 pt-2">
                      {r.piezas.map((p, i) => <PiezaBadge key={i} p={p} />)}
                    </ul>
                  )}
                  {r.bench && (
                    <>
                      <p className="mt-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-cyan-300"><ThermometerSun className="h-3 w-3" /> Pruebas de banco</p>
                      <Bench bench={r.bench} />
                    </>
                  )}
                </li>
              ))}
            </ol>
          )}
        </Seccion>

        {/* ¿Compraste este equipo? */}
        <Seccion icono={<BadgeCheck className="h-4 w-4" />} titulo="¿Compraste este equipo?" acento="text-amber-300">
          {resultado && (
            <div className={cn('mb-3 rounded-lg border px-3 py-2 text-sm', resultado.ok ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : 'border-rose-400/40 bg-rose-500/10 text-rose-200')}>
              {resultado.mensaje}
            </div>
          )}
          {!o.traspaso_activo ? (
            <p className="text-sm text-white/70">
              No hay un código de traspaso activo. Pida al dueño actual que genere uno desde su portal Pit Stop (sección «¿Vas a vender tu equipo?») o en el mostrador del taller, y compártaselo para reclamar el equipo aquí.
            </p>
          ) : (
            <div className="space-y-2.5">
              <p className="text-sm text-white/70">El dueño actual generó un código de traspaso. Complete sus datos para registrarse como nuevo dueño; la garantía vigente pasa con el equipo.</p>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-white/70">Código de traspaso (8 caracteres)</span>
                <input value={form.codigo} maxLength={8} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                  placeholder="XXXXXXXX" className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 font-mono text-sm uppercase tracking-widest text-white placeholder:text-white/30 focus:border-fuchsia-400 focus:outline-none" />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-white/70">Nombres y apellidos</span>
                <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400 focus:outline-none" />
              </label>
              <div className="grid grid-cols-2 gap-2.5">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-white/70">Cédula, RUC o pasaporte</span>
                  <input value={form.identificacion} onChange={(e) => setForm({ ...form, identificacion: e.target.value })}
                    className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400 focus:outline-none" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-white/70">Celular</span>
                  <input value={form.celular} onChange={(e) => setForm({ ...form, celular: e.target.value })}
                    className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400 focus:outline-none" />
                </label>
              </div>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-white/70">Correo (opcional)</span>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400 focus:outline-none" />
              </label>
              <label className="flex items-start gap-2 pt-1">
                <input type="checkbox" checked={form.acepta} onChange={(e) => setForm({ ...form, acepta: e.target.checked })} className="mt-0.5 h-4 w-4 accent-fuchsia-500" />
                <span className="text-xs text-white/60">Acepto el tratamiento de mis datos personales para registrar el traspaso de este equipo (LOPDP).</span>
              </label>
              <button onClick={() => reclamar.mutate()} disabled={!puedeEnviar || reclamar.isPending}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-violet-600 py-2.5 text-sm font-bold shadow-lg disabled:opacity-50">
                {reclamar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Reclamar este equipo
              </button>
            </div>
          )}
        </Seccion>

        <p className="flex items-center justify-center gap-1.5 pt-2 text-center text-[11px] text-white/30"><CalendarClock className="h-3 w-3" /> Pasaporte público del equipo — sin datos del dueño</p>
      </main>
    </div>
  );
}
